import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

/** Info about the currently-published Visarc Timer Windows installer.
 *  Returned by `getInfo()` once `/appdownload/latest.yml` has been fetched. */
export interface TimerAppDownloadInfo {
    /** Semver version pulled from `latest.yml` (e.g. `'0.1.2'`). */
    version: string;
    /** Public URL for the installer (e.g. `/appdownload/VisarcTimer-Setup-0.1.2.exe`). */
    url: string;
}

/**
 * Reads the currently-published Visarc Timer installer's version and URL
 * straight from the `latest.yml` manifest that electron-updater already
 * publishes alongside each `.exe`. The same file the desktop app's
 * auto-updater uses, so there's exactly one source of truth — no manual sync
 * needed when a new version is released.
 *
 * `latest.yml` is a small text file with a fixed shape:
 *
 *   version: 0.1.2
 *   files:
 *     - url: VisarcTimer-Setup-0.1.2.exe
 *       sha512: ...
 *       size: ...
 *   path: VisarcTimer-Setup-0.1.2.exe
 *   sha512: ...
 *   releaseDate: '2026-...'
 *
 * We pull `version` and the top-level `path` field with simple regexes — no
 * point pulling in a YAML parser dep for two single-line lookups.
 *
 * Returns `null` (instead of erroring) when the manifest is missing or
 * malformed — the settings page hides the download section in that case.
 */
@Injectable({ providedIn: 'root' })
export class TimerAppDownloadService {
    /** Installer files live outside `wwwroot` so they survive code
     *  deploys (otherwise every deploy wipes them and the Download
     *  button on `/users/settings` 404s until somebody re-uploads).
     *  Served from a configured directory via the `/appdownload`
     *  StaticFiles mount on the backend (`DesktopAppDirectory`
     *  config key). */
    private static readonly MANIFEST_URL = '/appdownload/latest.yml';

    /** localStorage key for tracking which timer-app version the user has
     *  acknowledged (by clicking the Download button). Drives the "new
     *  version" badge shown on the settings nav link, the settings page's
     *  Download button, and the timer-tracker page's download icon. */
    private static readonly SEEN_VERSION_KEY = 'visarc_timer_app_seen_version';

    /** Cached so opening /users/settings doesn't refetch on every navigation. */
    private cached$: Observable<TimerAppDownloadInfo | null> | null = null;

    /** Reactive store of the user's most-recently-acknowledged version
     *  (initialized from localStorage). Pushed to by `markDownloaded()` so
     *  any UI subscribed via `newVersionAvailable$` re-evaluates and clears
     *  immediately when the user clicks Download — the nav badge stays in
     *  sync without needing a page refresh. */
    private seenVersion$ = new BehaviorSubject<string | null>(this.readSeenVersion());

    constructor(private http: HttpClient) {}

    /** True when the published `version` differs from the one the user last
     *  acknowledged. Synchronous version — used by template bindings that
     *  re-evaluate every change-detection cycle (e.g. the settings page's
     *  download button). For long-lived subscriptions like the nav badge,
     *  use `newVersionAvailable$` instead, which is reactive. */
    hasNewVersion(version: string): boolean {
        return this.readSeenVersion() !== version;
    }

    /** Reactive: emits true while there's an unseen newer Timer-app version,
     *  false otherwise. Combines the published version (from latest.yml)
     *  with the seen-version subject, so calling `markDownloaded()`
     *  triggers a re-emission and the badge clears immediately. */
    newVersionAvailable$(): Observable<boolean> {
        return combineLatest([this.getInfo(), this.seenVersion$]).pipe(
            map(([info, seen]) => !!info && info.version !== seen),
        );
    }

    /** Records that the user has clicked the Download button for the given
     *  version. Persists to localStorage AND pushes to the reactive subject
     *  so subscribers of `newVersionAvailable$` see the change. */
    markDownloaded(version: string): void {
        try {
            localStorage.setItem(TimerAppDownloadService.SEEN_VERSION_KEY, version);
        } catch {
            // localStorage unavailable — no-op, the user still got the download.
        }
        this.seenVersion$.next(version);
    }

    private readSeenVersion(): string | null {
        try {
            return localStorage.getItem(TimerAppDownloadService.SEEN_VERSION_KEY);
        } catch {
            return null;
        }
    }

    getInfo(): Observable<TimerAppDownloadInfo | null> {
        if (!this.cached$) {
            this.cached$ = this.http
                .get(TimerAppDownloadService.MANIFEST_URL, { responseType: 'text' })
                .pipe(
                    map(yaml => this.parse(yaml)),
                    catchError(() => of(null)),
                    shareReplay(1),
                );
        }
        return this.cached$;
    }

    private parse(yaml: string): TimerAppDownloadInfo | null {
        const versionMatch = yaml.match(/^version:\s*(.+)$/m);
        const pathMatch = yaml.match(/^path:\s*(.+)$/m);
        if (!versionMatch || !pathMatch) return null;
        return {
            version: versionMatch[1].trim().replace(/^['"]|['"]$/g, ''),
            url: `/appdownload/${pathMatch[1].trim().replace(/^['"]|['"]$/g, '')}`,
        };
    }
}
